import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Seo, { orgLd, websiteLd, breadcrumbLd, personLd, localBusinessLd } from "../Seo";
import { CONTACT } from "../../contact";
import { SHOW_CONTACT, FACTS_APPROVED } from "../../companyFacts";
import { DEPARTMENTS } from "../../departments";
import { trackFormStart, trackFormSubmit, trackLead } from "../../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IMG = "/assets/";

/* ---------- helpers ---------- */
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const els = ref.current ? ref.current.querySelectorAll(".rv") : [];
    if (!("IntersectionObserver" in window)) { els.forEach((e) => e.classList.add("in")); return; }
    const io = new IntersectionObserver((ents) => {
      ents.forEach((e, i) => { if (e.isIntersecting) { e.target.classList.add("in"); io.unobserve(e.target); } });
    }, { threshold: 0.18 });
    els.forEach((e) => io.observe(e));
    return () => io.disconnect();
  }, []);
  return ref;
}

/* ---------- data ---------- */
const CATS = [
  ["01", "Garments & Apparel", "Woven, knits and made-ups developed and quality-checked across India's textile clusters.", "garment-textile-sourcing-india.webp", "/garment-manufacturers-india", "garment-apparel"],
  ["02", "Home Furnishing", "Bedding, cushions, curtains and soft furnishings from Panipat, Karur and Jaipur.", "home-furnishing-sourcing-india.webp", "/home-furnishing-manufacturers-india", "home-furnishing"],
  ["03", "Imitation Jewellery", "Fashion and imitation jewellery developed to design, with finishing and QC managed on the ground.", "imitation-jewellery-sourcing-india.webp", "/imitation-fashion-jewellery-manufacturers-india", "imitation-jewellery"],
  ["04", "Plastic Household", "Household, storage and garden plastic products developed with Indian manufacturers.", "plastic-products-sourcing-india.webp", "/plastic-products-manufacturers-india", "plastic-household"],
  ["05", "Stainless Steel Houseware", "Utensils, cookware and tableware from India's stainless steel manufacturing sector.", "stainless-steel-houseware-india.webp", "/stainless-steel-utensil-manufacturers-india", "stainless-steel-houseware"],
  ["06", "Cutlery & Flatware", "Spoons, forks and flatware manufactured and polished to export standard.", "cutlery-flatware-sourcing-india.webp", "/cutlery-manufacturers-india", "cutlery-flatware"],
  ["07", "Carpets & Rugs", "Hand-knotted, handwoven and tufted carpets and rugs from Bhadohi, Mirzapur and Panipat.", "carpet-rug-sourcing-india.webp", "/carpet-manufacturers-india", "carpets-rugs"],
];
const EXTRA = [
  ["Furniture", "/furniture-manufacturers-india"],
  ["Lighting", "/what-we-source/lighting"],
  ["Handicrafts", "/handicraft-manufacturers-india"],
  ["Hard Goods", "/hard-goods-sourcing-india"],
];

const SERVICES = [
  ["01", "Product Development", "From a sketch, reference or tech pack to a buildable specification, counter-samples and a signed golden sample.", "/product-development-india"],
  ["02", "Factory Sourcing", "We identify the right cluster and the right manufacturing partner by capability and capacity — not the lowest quote.", "/factory-sourcing-india"],
  ["03", "Quality Assurance", "Five checkpoints from factory audit to container loading, inspected to AQL with a full photographic record.", "/quality-control-india"],
];
const MORE_SERVICES = [
  ["Factory Inspection", "/factory-audit-india"],
  ["Supplier Verification", "/supplier-verification-india"],
  ["Production Monitoring", "/quality-control-india"],
  ["Pre-Shipment Inspection", "/quality-control-india"],
  ["Private Label Sourcing", "/private-label-manufacturing-india"],
  ["Export Management", "/export-management-india"],
];

const CLUSTERS = [
  ["Moradabad", "Brassware & metal décor", 46, 30],
  ["Jaipur", "Block-print textiles & handicraft", 32, 40],
  ["Panipat", "Home textiles & blankets", 40, 25],
  ["Saharanpur", "Carved woodware & furniture", 44, 22],
  ["Firozabad", "Glassware & lighting", 50, 35],
  ["Noida", "Garments & stainless steel", 42, 29],
  ["Gurgaon", "Apparel & hardlines", 38, 31],
  ["Varanasi", "Silk textiles & carpets", 62, 41],
  ["Jodhpur", "Wood & wrought-iron furniture", 24, 42],
  ["Chhatarpur", "Stone crafts & ceramics", 50, 50],
];

const QC = [
  ["01", "Factory Audit", "Compliance, capacity, legal verification and machine capability assessed before any order is placed."],
  ["02", "Pre-Production", "Raw material testing, colour matching, lab-dip approval and sample sign-off before production begins."],
  ["03", "During Production", "In-line audit at 15–30% completion to catch systematic defects while they can still be corrected."],
  ["04", "Pre-Shipment", "Final random inspection at 80%+ packed, sampled to AQL (ISO 2859-1), with a full photographic record."],
  ["05", "Loading", "Container loading supervision — box count, seal integrity, weight distribution and export documentation."],
];

/* India outline (stylised, viewBox 0 0 100 100) */
const INDIA = "M40 10 L50 12 L56 10 L58 16 L66 18 L62 26 L70 30 L66 36 L72 40 L64 44 L66 52 L58 58 L60 66 L52 72 L50 84 L44 74 L44 64 L38 60 L34 50 L28 46 L24 38 L30 34 L26 26 L32 22 L30 14 L40 10 Z";

const MARKETS = [["USA", 40, 42], ["UK", 92, 32], ["Europe", 100, 36], ["Australia", 165, 82]];

/* ---------- sections ---------- */
function Hero() {
  return (
    <section className="rd-hero" id="top" data-testid="rd-hero">
      <div className="bg">
        <picture>
          <source media="(max-width: 640px)" srcSet={IMG + "sourcing-india-garment-home-houseware-sourcing-india-mobile.webp"} />
          <img src={IMG + "sourcing-india-garment-home-houseware-sourcing-india.webp"} alt="Premium garment, home and houseware product sourcing in India" width="1264" height="848" fetchPriority="high" />
        </picture>
      </div>
      <div className="inner">
        <p className="kick">Sourcing India · Your sourcing partner in India</p>
        <h1>
          <span className="hln"><i>Garment, home &amp;</i></span>
          <span className="hln"><i>houseware sourcing</i></span>
          <span className="hln"><i>from India.</i></span>
        </h1>
        <p className="hsub">End-to-end sourcing and quality control for global brands across 14 product categories, from garments, home furnishings and stainless steel to glassware, woodware, fragrance and jewellery. We work for the buyer, not the factory.</p>
        <div className="btnrow">
          <Link className="btnp" to="/#contact" data-testid="hero-cta-start-project">Start a Sourcing Project <ArrowRight className="ar" size={16} /></Link>
          <Link className="btns" to="/#what-we-source" data-testid="hero-cta-explore-categories">Explore What We Source</Link>
        </div>
      </div>
      <span className="rd-scroll">Scroll</span>
    </section>
  );
}

function HealthPromo() {
  return (
    <section className="rd-hpromo" aria-label="Free sourcing health check">
      <div className="wrapx rd-hpromo-in">
        <div>
          <p className="kick" style={{ color: "#B56E55" }}>Free · 3 minutes · No obligation</p>
          <h2>How strong is your India sourcing strategy?</h2>
          <p>Answer ten questions and see where your supplier network, product development, quality control and production control could be strengthened.</p>
        </div>
        <div className="btnrow" style={{ justifyContent: "flex-end" }}>
          <Link className="btnp" to="/sourcing-stress-test" data-testid="hp-health-check">Take the free stress test <ArrowRight className="ar" size={16} /></Link>
        </div>
      </div>
    </section>
  );
}

function About() {
  return (
    <section className="rd-about" id="about">
      <div className="wrapx"><div className="cols">
        <div className="fig rv">
          <img src={IMG + "sourcing-specialist-india.webp"} alt="Sourcing specialist reviewing a product tech pack and material swatches in India" width="848" height="1264" loading="lazy" />
          <span className="tag">We work for the buyer</span>
        </div>
        <div className="rv">
          <p className="kick">About Sourcing India</p>
          <h2>A buyer-side sourcing &amp; quality-control partner in India.</h2>
          <p className="lead">Sourcing India is a buyer-side sourcing and quality-control company based in India. We develop products, select and verify factories, manage production and inspect quality on behalf of international buyers.</p>
          <p>From the first product brief to the loaded container, we connect global brands, retailers, importers and e-commerce companies with the right manufacturing partners across India — and stay on the ground to manage the detail. We are not a factory, a marketplace or a customs broker.</p>
          <ul className="bullets">
            <li>Product development &amp; golden samples</li>
            <li>Factory sourcing &amp; supplier verification</li>
            <li>Production monitoring &amp; inspection</li>
            <li>Private-label &amp; vendor management</li>
          </ul>
          <div className="btnrow" style={{ marginTop: "30px" }}>
            <Link className="btns" to="/#services">View our services <ArrowRight className="ar" size={15} /></Link>
          </div>
        </div>
      </div></div>
    </section>
  );
}

function WhatWeSource() {
  return (
    <section className="rd-wws" id="what-we-source">
      <div className="wrapx">
        <div className="top">
          <div className="shead rv">
            <p className="kick">What we source</p>
            <h2>14 product categories in 6 departments, one sourcing partner.</h2>
          </div>
          <p className="rv" style={{ maxWidth: "40ch" }}>Textiles and apparel, kitchen and dining, fragrance and wellness, wooden and natural products, decorative products and fashion accessories, sourced from verified manufacturers across India.</p>
        </div>
        <div className="rd-cards">
          {CATS.map((c) => (
            <Link className="rd-cat rv" to={c[4]} key={c[1]} data-testid={"category-card-" + c[5]}>
              <img src={IMG + c[3]} alt={c[1] + " sourcing from India"} loading="lazy" />
              <span className="cc">
                <span className="idx">{c[0]}</span>
                <span className="nm">{c[1]}</span>
                <span className="ds">{c[2]}</span>
                <span className="go">Explore sourcing <ArrowUpRight size={14} /></span>
              </span>
            </Link>
          ))}
        </div>
        <div className="rd-depts rv" data-testid="home-departments">
          {DEPARTMENTS.map((g) => (
            <div className="rd-extra" key={g.id}>
              <span>{g.name}</span>
              {g.items.map(([t, to]) => <Link key={to} to={to} data-testid={"home-dept-" + to.slice(1)}>{t}</Link>)}
            </div>
          ))}
          <div className="rd-extra"><span>Also sourcing</span>{EXTRA.map((e) => <Link key={e[0]} to={e[1]}>{e[0]}</Link>)}</div>
        </div>
        <div className="rv" style={{ marginTop: "40px", textAlign: "center" }}>
          <p className="kick" style={{ justifyContent: "center" }}>What can we source for you?</p>
          <p className="lead" style={{ maxWidth: "52ch", margin: "14px auto 0" }}>From garments and home furnishing to candles, leather goods, stationery, lighting and houseware — we connect international buyers with verified Indian manufacturing partners.</p>
          <div className="btnrow" style={{ justifyContent: "center", marginTop: "24px" }}>
            <Link className="btnp" to="/what-we-source" data-testid="explore-all-categories">Explore All Sourcing Categories <ArrowRight className="ar" size={16} /></Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function Services() {
  return (
    <section className="rd-serv" id="services">
      <div className="wrapx">
        <div className="shead rv">
          <p className="kick">Services</p>
          <h2>From the first brief to the final shipment.</h2>
        </div>
        <div className="grid">
          {SERVICES.map((s) => (
            <Link className="rd-sv rv" to={s[3]} key={s[0]} style={{ textDecoration: "none" }}>
              <div className="idx">{s[0]}</div>
              <h3>{s[1]}</h3>
              <p>{s[2]}</p>
            </Link>
          ))}
        </div>
        <div className="more rv">
          {MORE_SERVICES.map((m) => <Link key={m[0]} to={m[1]}>{m[0]}</Link>)}
        </div>
      </div>
    </section>
  );
}

function FactoryNetwork() {
  const [on, setOn] = useState(0);
  return (
    <section className="rd-net" id="factory-network">
      <div className="wrapx">
        <div className="shead rv">
          <p className="kick">Factory network</p>
          <h2>Verified Factories. One Sourcing Partner.</h2>
          <p className="lead">From product development and factory selection to quality control and shipment, we connect global buyers with the right manufacturing partners across India's specialist clusters.</p>
        </div>
        <div className="cols">
          <div className="rd-map rv">
            <svg viewBox="0 0 100 100" role="img" aria-label="Map of Indian manufacturing clusters">
              <path className="mshape" d={INDIA} />
              {CLUSTERS.map((c, i) => (
                <g className={"rd-mk" + (on === i ? " on" : "")} key={c[0]} transform={`translate(${c[2]},${c[3]})`}
                  onMouseEnter={() => setOn(i)} data-testid={"factory-cluster-" + c[0].toLowerCase()}>
                  <circle className="ring" r="3.4"><animate attributeName="r" values="1.6;4;1.6" dur="2.6s" begin={i * 0.2 + "s"} repeatCount="indefinite" /><animate attributeName="opacity" values=".55;0;.55" dur="2.6s" begin={i * 0.2 + "s"} repeatCount="indefinite" /></circle>
                  <circle className="dot" r="1.5" />
                  <text className="lbl" x="3" y="1.2">{c[0]}</text>
                </g>
              ))}
            </svg>
          </div>
          <div className="rv">
            <ul className="rd-clist">
              {CLUSTERS.map((c, i) => (
                <li key={c[0]} onMouseEnter={() => setOn(i)}><b>{c[0]}</b><span>{c[1]}</span></li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function GlobalBuyers() {
  return (
    <section className="rd-glob" id="global-buyers">
      <div className="wrapx">
        <div className="shead rv">
          <p className="kick">Global buyer network</p>
          <h2>Sourcing for International Buyers.</h2>
          <p className="lead">Connecting Indian manufacturing with brands, retailers, importers and e-commerce buyers around the world — with India as the sourcing hub.</p>
        </div>
        <div className="rd-world rv">
          <svg viewBox="0 0 200 100" role="img" aria-label="Sourcing India connecting to global buyer markets">
            {MARKETS.map((m) => (
              <path className="wline" key={m[0]} d={`M150 55 Q${(150 + m[1]) / 2} ${Math.min(m[2], 55) - 24} ${m[1]} ${m[2]}`} />
            ))}
            <circle className="hub" cx="150" cy="55" r="3.4" />
            <text x="150" y="64" textAnchor="middle" fontSize="4" fill="#B56E55" fontFamily="Inter">India</text>
            {MARKETS.map((m) => (
              <g key={m[0]}>
                <circle className="wdot" cx={m[1]} cy={m[2]} r="2.2" />
                <text x={m[1]} y={m[2] - 4} textAnchor="middle" fontSize="3.6" fill="#F8F7F2" fontFamily="Inter">{m[0]}</text>
              </g>
            ))}
          </svg>
        </div>
        <div className="mk rv">
          <Link to="/usa-india-sourcing">USA</Link>
          <Link to="/uk-india-sourcing">UK</Link>
          <Link to="/germany-india-sourcing">Germany</Link>
          <Link to="/europe-india-sourcing">Europe</Link>
          <Link to="/international-sourcing">All markets</Link>
        </div>
      </div>
    </section>
  );
}

function Quality() {
  const [lit, setLit] = useState(false);
  const tl = useRef(null);
  useEffect(() => {
    const io = new IntersectionObserver((es) => { if (es[0].isIntersecting) { setLit(true); io.disconnect(); } }, { threshold: 0.3 });
    if (tl.current) io.observe(tl.current);
    return () => io.disconnect();
  }, []);
  return (
    <section className="rd-qc" id="quality">
      <div className="wrapx">
        <div className="shead rv">
          <p className="kick">Quality control</p>
          <h2>Five checkpoints, not one inspection at the end.</h2>
          <p className="lead">Quality control that works happens while the outcome can still change — across five stages from factory audit to container loading.</p>
        </div>
        <div className={"rd-tl" + (lit ? " in" : "")} ref={tl}>
          <div className="bar" aria-hidden="true"></div>
          <div className="barf" aria-hidden="true"></div>
          <div className="rd-steps">
            {QC.map((s, i) => (
              <div className={"rd-step" + (lit ? " in" : "")} key={s[0]} style={{ transitionDelay: i * 0.12 + "s" }} data-testid={"qc-timeline-stage-" + (i + 1)}>
                <div className="n">{s[0]}</div>
                <div><h3>{s[1]}</h3><p>{s[2]}</p></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Founder() {
  return (
    <section className="rd-fnd" id="founder">
      <div className="wrapx"><div className="cols">
        <div className="fig rv">
          <img src={IMG + "dr-sumit-chhabra-founder-sourcing-india.webp?v=3"} alt="Dr. Sumit Chhabra, Founder and CEO of Sourcing India" width="1269" height="1240" loading="lazy" />
        </div>
        <div className="rv">
          <p className="kick">Meet the founder</p>
          <blockquote>"Not the cheapest factory, but the right one — for the product, the quality and the market."</blockquote>
          <p>Dr. Sumit Chhabra is the Founder &amp; CEO of Sourcing India. With hands-on experience in international sourcing and manufacturing, he has developed deep working knowledge of India's manufacturing ecosystem — from identifying the right clusters and factories to product development, sampling, quality control and export coordination.</p>
          <p>His approach is simple: understand the product and the buyer first, then find the manufacturing partner that is right for the quality, the market and the long-term relationship.</p>
          <p className="rd-sig" data-testid="founder-name">Dr. Sumit Chhabra<span data-testid="founder-role">Founder &amp; CEO · Sourcing India</span></p>
          <div className="btnrow" style={{ marginTop: "22px" }}>
            {SHOW_CONTACT && <a className="btns" href={CONTACT.waSam} target="_blank" rel="noopener noreferrer">Talk to Sourcing India</a>}
          </div>
        </div>
      </div></div>
    </section>
  );
}

function CTA() {
  return (
    <section className="rd-cta">
      <div className="bg"><img src={IMG + "india-sourcing-export-hub.webp"} alt="Indian manufacturing and export hub at dusk" loading="lazy" /></div>
      <div className="wrapx"><div className="inner">
        <p className="kick" style={{ justifyContent: "center", color: "#B56E55" }}>Start here</p>
        <h2>Tell us what you want to build.</h2>
        <p>A photo, a reference product or a tech pack is enough to start. We reply within one working day.</p>
        <div className="btnrow">
          <Link className="btnp" to="/#contact">Start a Sourcing Project <ArrowRight className="ar" size={16} /></Link>
          <Link className="btns" to="/#what-we-source">Explore What We Source</Link>
        </div>
      </div></div>
    </section>
  );
}

const CAT_OPTS = ["Garments & Apparel", "Home Furnishing", "Imitation Jewellery", "Plastic Household", "Stainless Steel Houseware", "Cutlery & Flatware", "Carpets & Rugs", "Home Décor", "Furniture", "Lighting", "Handicrafts", "Other"];

function Contact() {
  const [status, setStatus] = useState("idle");
  const noteRef = useRef(null);
  useEffect(() => {
    if (["sent", "error", "required", "consent"].includes(status)) noteRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [status]);
  const submit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    if (!String(fd.get("name") || "").trim() || !String(fd.get("email") || "").trim()
      || !String(fd.get("category") || "").trim() || !String(fd.get("product") || "").trim()) {
      setStatus("required"); return;
    }
    if (!fd.get("consent")) { setStatus("consent"); return; }
    trackFormSubmit("home-contact", fd.get("category") || "");
    const msg = `Product: ${fd.get("product") || ""}\nTarget price: ${fd.get("price") || "—"}\nQuantity: ${fd.get("qty") || "—"}`;
    const payload = {
      name: fd.get("name") || "", company: fd.get("company") || "", email: fd.get("email") || "",
      phone: "", country: fd.get("country") || "", category: fd.get("category") || "",
      qty: fd.get("qty") || "", reference: "", needs: [], message: msg,
      page_url: typeof window !== "undefined" ? window.location.href : "", website: fd.get("website") || "",
    };
    setStatus("sending");
    try { await axios.post(`${API}/inquiries`, payload); setStatus("sent"); form.reset(); trackLead("home-contact", payload.category); }
    catch { setStatus("error"); }
  };
  const note = {
    idle: ["Required: Name, Email, Category and Product. We reply within one working day.", "var(--ink-55)"],
    sending: ["Sending…", "var(--ink-55)"],
    sent: ["Thank you — our sourcing team will review your requirement and reply within one working day.", "#1F7A54"],
    error: ["Sorry, we couldn't submit right now. Please try again or email us directly.", "#B23B3B"],
    required: ["Please complete Name, Email, Category and Product.", "#B23B3B"],
    consent: ["Please tick the consent box so we can reply to your enquiry.", "#B23B3B"],
  }[status];
  return (
    <section className="rd-contact" id="contact">
      <div className="wrapx"><div className="cols">
        <div className="rv">
          <p className="kick">Contact</p>
          <h2>Tell us what you want to build.</h2>
          <p className="lead">A photo, reference product or tech pack is enough to start.</p>
          <dl className="rd-dl">
            {SHOW_CONTACT && <div className="r"><dt>Email</dt><dd><a href={"mailto:" + CONTACT.email}>{CONTACT.email}</a></dd></div>}
            {SHOW_CONTACT && <div className="r"><dt>Phone &amp; WhatsApp</dt><dd><a href={"tel:" + CONTACT.phoneRaw}>{CONTACT.phone}</a></dd></div>}
            {FACTS_APPROVED.address && <div className="r"><dt>Office</dt><dd>D-8, Manarover Colony, Delhi Road,<br />Moradabad, Uttar Pradesh 244001, India</dd></div>}
            {SHOW_CONTACT && <div className="r"><dt>Hours</dt><dd>Mon–Sat, 09:30–19:00 IST</dd></div>}
          </dl>
        </div>
        <form className="rd-form rv" noValidate onSubmit={submit} onFocusCapture={() => trackFormStart("home-contact")} data-testid="contact-form">
          <div className="f"><label htmlFor="c-name">Name</label><input id="c-name" name="name" autoComplete="name" data-testid="contact-name" /></div>
          <div className="f"><label htmlFor="c-company">Company</label><input id="c-company" name="company" autoComplete="organization" /></div>
          <div className="f"><label htmlFor="c-email">Email</label><input id="c-email" name="email" type="email" autoComplete="email" data-testid="contact-email" /></div>
          <div className="f"><label htmlFor="c-country">Country</label><input id="c-country" name="country" autoComplete="country-name" /></div>
          <div className="f"><label htmlFor="c-cat">Category</label>
            <select id="c-cat" name="category" defaultValue="" data-testid="contact-category">
              <option value="" disabled>Select a category</option>
              {CAT_OPTS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div className="f"><label htmlFor="c-qty">Quantity</label><input id="c-qty" name="qty" placeholder="e.g. 2,000 pcs" /></div>
          <div className="f full"><label htmlFor="c-prod">Product</label><textarea id="c-prod" name="product" placeholder="Describe the product — or paste a link / reference." data-testid="contact-product" /></div>
          <div className="f"><label htmlFor="c-price">Target price</label><input id="c-price" name="price" placeholder="e.g. $4.50 / pc FOB" /></div>
          <input className="hp" type="text" name="website" tabIndex="-1" autoComplete="off" aria-hidden="true" />
          <label className="consent"><input type="checkbox" name="consent" data-testid="contact-consent" /> I agree to Sourcing India contacting me about my enquiry. We never share your details.</label>
          <div className="sub">
            <button className="btnp" type="submit" disabled={status === "sending"} data-testid="contact-form-submit-button">
              {status === "sending" ? "Sending…" : "Start a Sourcing Project"} <ArrowRight className="ar" size={16} />
            </button>
            <span className="rd-note" ref={noteRef} style={{ color: note[1] }} data-testid="contact-status" role="status">{note[0]}</span>
          </div>
        </form>
      </div></div>
    </section>
  );
}

export default function HomeRedesign() {
  const ref = useReveal();
  return (
    <main className="rd" ref={ref}>
      <Seo
        title="India Sourcing Company | Factory Audits & Quality Control"
        description="Source products from India with factory audits, supplier verification, product development and quality control for international buyers and brands."
        path="/"
        jsonLd={[orgLd, websiteLd, localBusinessLd, ...(FACTS_APPROVED.founderTitle ? [personLd] : []), breadcrumbLd([{ name: "Home", path: "/" }])]}
      />
      <Hero />
      <HealthPromo />
      <About />
      <WhatWeSource />
      <Services />
      <FactoryNetwork />
      <GlobalBuyers />
      <Quality />
      {FACTS_APPROVED.founderTitle && <Founder />}
      <CTA />
      <Contact />
    </main>
  );
}
